import type { BasicIntlShape } from './intl'

const SOURCE_LOGO_BASE =
  'https://storage.googleapis.com/slite-cdn/logos/colored'

// The connector logos shown wherever we advertise "search across your tools".
export const SLITE_CONNECTOR_ICONS = [
  `${SOURCE_LOGO_BASE}/slack_channel_mark.svg`,
  `${SOURCE_LOGO_BASE}/hubspot_mark.svg`,
  `${SOURCE_LOGO_BASE}/linear_mark.svg`,
  `${SOURCE_LOGO_BASE}/jira_mark.svg`,
  `${SOURCE_LOGO_BASE}/google_drive_mark.svg`,
  `${SOURCE_LOGO_BASE}/github_mark.svg`,
  `${SOURCE_LOGO_BASE}/asana_mark.svg`,
  `${SOURCE_LOGO_BASE}/attio_mark.svg`,
  `${SOURCE_LOGO_BASE}/intercom_mark.svg`,
  `${SOURCE_LOGO_BASE}/bigquery_mark.svg`,
  `${SOURCE_LOGO_BASE}/salesforce_mark.svg`,
]

// A plan cell: true = included, false = not, or custom copy.
export type PlanCell = boolean | string

export type PricingFeatureItem = {
  title: string
  description?: string
  basic: PlanCell
  pro: PlanCell
  enterprise: PlanCell
  basicSubtext?: string
  // Hover tooltip shown on the Basic-cell subtext (no link, plain text).
  basicSubtextTooltip?: string
  sourceIcons?: string[]
  // Render the Basic cell greyed even when other plans include it.
  mutedBasic?: boolean
}

export type PricingFeatureGroup = {
  id: string
  title: string
  items: PricingFeatureItem[]
}

// Canonical Slite feature matrix, shared by the marketing pricing page and the
// in-app upgrade comparison so the two never drift. Columns are Basic/Pro/Enterprise.
// Takes the consumer's intl so labels are localized once, in one place.
export function getSlitePricingFeatures(
  intl: BasicIntlShape
): PricingFeatureGroup[] {
  return [
    {
      id: 'editorDocs',
      title: intl.formatMessage({
        id: 'pricing.features.editorDocs.title',
        defaultMessage: 'Editor & docs',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.editor.title',
            defaultMessage: 'Real-time collaborative editor',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.editor.description',
            defaultMessage:
              'Real-time multiplayer editing with rich formatting, embeds, and inline comments.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.comments.title',
            defaultMessage: 'Comments, mentions & notifications',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.comments.description',
            defaultMessage:
              'Comment on docs, @-mention teammates, and get notified when there is activity.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.templates.title',
            defaultMessage: 'Custom templates',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.templates.description',
            defaultMessage:
              'Create reusable templates so your team writes consistently across docs.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.recurring.title',
            defaultMessage: 'Schedule recurring docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.recurring.description',
            defaultMessage:
              'Auto-create docs on a schedule for recurring meetings, retros, or reports.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.history.title',
            defaultMessage: 'Doc history',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.history.description',
            defaultMessage: 'Restore previous versions of any doc.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.unlimitedDocs.title',
            defaultMessage: 'Unlimited docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.unlimitedDocs.description',
            defaultMessage:
              'Create as many docs as your team needs across all plans.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.fileUpload.title',
            defaultMessage: 'File upload',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.fileUpload.description',
            defaultMessage:
              'Maximum size for any single file uploaded to a doc.',
          }),
          basic: intl.formatMessage({
            id: 'pricing.features.editorDocs.fileUpload.basic',
            defaultMessage: 'Up to 200MB',
          }),
          pro: intl.formatMessage({
            id: 'pricing.features.editorDocs.fileUpload.core',
            defaultMessage: 'Unlimited',
          }),
          enterprise: intl.formatMessage({
            id: 'pricing.features.editorDocs.fileUpload.enterprise',
            defaultMessage: 'Unlimited',
          }),
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.storage.title',
            defaultMessage: 'Storage per user',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.storage.description',
            defaultMessage:
              'Total file storage included per user across the workspace.',
          }),
          basic: intl.formatMessage({
            id: 'pricing.features.editorDocs.storage.basic',
            defaultMessage: '5GB',
          }),
          pro: intl.formatMessage({
            id: 'pricing.features.editorDocs.storage.core',
            defaultMessage: '10GB',
          }),
          enterprise: intl.formatMessage({
            id: 'pricing.features.editorDocs.storage.enterprise',
            defaultMessage: '10GB',
          }),
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.editorDocs.exports.title',
            defaultMessage: 'PDF, Markdown, HTML exports',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.editorDocs.exports.description',
            defaultMessage: 'Export individual docs or the entire workspace.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      id: 'verification',
      title: intl.formatMessage({
        id: 'pricing.features.verification.title',
        defaultMessage: 'Verification & maintenance',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.verification.docVerification.title',
            defaultMessage: 'Doc verification',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.verification.docVerification.description',
            defaultMessage:
              "Mark docs as verified with a freshness state and expiration so your team knows what's trusted.",
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.verification.ownership.title',
            defaultMessage: 'Doc ownership',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.verification.ownership.description',
            defaultMessage:
              'Every doc has an assigned owner who is responsible for keeping it up to date.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.verification.kmp.title',
            defaultMessage: 'Knowledge Management Panel',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.verification.kmp.description',
            defaultMessage:
              'Centrally review, filter, and take bulk actions on docs across your knowledge base.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.verification.docInsights.title',
            defaultMessage: 'Doc insights',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.verification.docInsights.description',
            defaultMessage:
              'See views, contributors, and a full activity log for any doc.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      id: 'aiAgents',
      title: intl.formatMessage({
        id: 'pricing.features.aiAgents.title',
        defaultMessage: 'AI & agents',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.title',
            defaultMessage: 'AI search & answers',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.description',
            defaultMessage:
              'Get instant AI answers from your knowledge base in natural language.',
          }),
          sourceIcons: SLITE_CONNECTOR_ICONS,
          basic: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.basic',
            defaultMessage: 'Within Slite docs',
          }),
          basicSubtext: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.basic.subtext',
            defaultMessage: '30 monthly questions per seat',
          }),
          basicSubtextTooltip: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.basic.subtext.tooltip',
            defaultMessage: 'Resets on the 1st of each month.',
          }),
          pro: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.core',
            defaultMessage: 'Across connected tools',
          }),
          enterprise: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiSearch.enterprise',
            defaultMessage: 'Across connected tools',
          }),
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiWriting.title',
            defaultMessage: 'AI writing assistant',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiWriting.description',
            defaultMessage:
              'Use AI to draft, summarize, fix spelling, change tone, and rewrite content directly in the editor.',
          }),
          basic: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiWriting.basic',
            defaultMessage: '30 AI edits/month/user',
          }),
          pro: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiWriting.core',
            defaultMessage: 'Unlimited',
          }),
          enterprise: intl.formatMessage({
            id: 'pricing.features.aiAgents.aiWriting.enterprise',
            defaultMessage: 'Unlimited',
          }),
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInsights.title',
            defaultMessage: 'AI answers insights',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInsights.description',
            defaultMessage:
              'See questions your team asked that went unanswered or got reported, so you can improve your knowledge base.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.chromeExt.title',
            defaultMessage: 'Chrome extension',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.chromeExt.description',
            defaultMessage: 'Search and ask questions from any browser tab.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.superDetect.title',
            defaultMessage: 'Doc fact-checking with suggested fixes',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.superDetect.description',
            defaultMessage:
              'Automatically flag stale docs and propose updates for human review.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.customAssistants.title',
            defaultMessage: 'Custom Assistants',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.customAssistants.description',
            defaultMessage:
              'Build presets of instructions and filters for your team to get predictable results.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.automatedDigests.title',
            defaultMessage: 'Automated digests',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.automatedDigests.description',
            defaultMessage:
              'Generate reports and updates, run them automatically and receive them in Slack or by email.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.bulkMode.title',
            defaultMessage: 'Bulk mode',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.bulkMode.description',
            defaultMessage:
              'Automate filling RFPs, security forms, and ask 100s of questions at once.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInSlack.title',
            defaultMessage: 'AI answers in Slack',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInSlack.description',
            defaultMessage:
              'Ask questions in Slack and get instant AI answers from your knowledge base.',
          }),
          basic: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInSlack.basic',
            defaultMessage: 'From Slite docs',
          }),
          pro: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInSlack.core',
            defaultMessage: 'Across connected tools',
          }),
          enterprise: intl.formatMessage({
            id: 'pricing.features.aiAgents.askInSlack.enterprise',
            defaultMessage: 'Across connected tools',
          }),
        },
      ],
    },
    {
      id: 'integrations',
      title: intl.formatMessage({
        id: 'pricing.features.integrations.title',
        defaultMessage: 'Integrations & API',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.publicApi.title',
            defaultMessage: 'Public API',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.publicApi.description',
            defaultMessage:
              "Build custom integrations and automations using Slite's REST API.",
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.serviceAccounts.title',
            defaultMessage: 'Service accounts',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.serviceAccounts.description',
            defaultMessage:
              'Workspace-owned accounts with scoped API keys for integrations, automations, and AI agents. No seat required.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.embeds.title',
            defaultMessage: 'Embeds & link previews',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.embeds.description',
            defaultMessage:
              'Embed live previews from many tools and any public URL.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.authenticatedEmbeds.title',
            defaultMessage: 'Authenticated embeds',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.authenticatedEmbeds.description',
            defaultMessage:
              'Embed private content from Google Workspace, Linear, Asana, Slack, GitHub, Jira, Confluence, and Microsoft.',
          }),
          mutedBasic: true,
          sourceIcons: [
            `${SOURCE_LOGO_BASE}/google_drive_mark.svg`,
            `${SOURCE_LOGO_BASE}/linear_mark.svg`,
            `${SOURCE_LOGO_BASE}/asana_mark.svg`,
            `${SOURCE_LOGO_BASE}/slack_channel_mark.svg`,
            `${SOURCE_LOGO_BASE}/github_mark.svg`,
            `${SOURCE_LOGO_BASE}/jira_mark.svg`,
            `${SOURCE_LOGO_BASE}/confluence_mark.svg`,
            `${SOURCE_LOGO_BASE}/microsoft_mark.svg`,
          ],
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.slack.title',
            defaultMessage: 'Slack integration',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.slack.description',
            defaultMessage:
              'Set up personal Slite notifications, sync Slite channel updates to Slack channels, and search Slite content from Slack.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.integrations.mcp.title',
            defaultMessage: 'MCP access',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.integrations.mcp.description',
            defaultMessage:
              'Connect Slite as an MCP server so agents and AI tools can read and write docs.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      id: 'sharing',
      title: intl.formatMessage({
        id: 'pricing.features.sharing.title',
        defaultMessage: 'Sharing & collaboration',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.publicSharing.title',
            defaultMessage: 'Public sharing',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.publicSharing.description',
            defaultMessage: 'Publish any doc as a public webpage.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.aiOnPublic.title',
            defaultMessage: 'AI answers on public docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.aiOnPublic.description',
            defaultMessage:
              'Let visitors of your published docs ask questions and get instant AI answers.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.seo.title',
            defaultMessage: 'SEO indexing for public docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.seo.description',
            defaultMessage:
              'Make published docs discoverable by search engines.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.customBranding.title',
            defaultMessage: 'Custom branding & domain for public docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.customBranding.description',
            defaultMessage:
              'Hide Slite branding from public docs and use your own domain (e.g. docs.yourcompany.com).',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.guests.title',
            defaultMessage: 'Guest collaborators',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.guests.description',
            defaultMessage:
              'Invite external collaborators to specific docs without giving them workspace access.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.sharing.userGroups.title',
            defaultMessage: 'User groups',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.sharing.userGroups.description',
            defaultMessage:
              'Organize users into groups so you can mention them in comments, assign them as doc owners, or share docs and channels with them in one action.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      id: 'admin',
      title: intl.formatMessage({
        id: 'pricing.features.admin.title',
        defaultMessage: 'Admin',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.docPermissions.title',
            defaultMessage: 'Doc permissions',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.docPermissions.description',
            defaultMessage:
              'Set who can view, edit, or comment on docs at every level of your hierarchy.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.protectEditing.title',
            defaultMessage: 'Protect doc editing',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.protectEditing.description',
            defaultMessage: 'Prevent others from editing specific docs.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.readerAccess.title',
            defaultMessage: 'Reader access on docs',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.readerAccess.description',
            defaultMessage:
              'Add any teammate as read-only when sharing a specific doc.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.readerSeats.title',
            defaultMessage: 'Reader-only seats',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.readerSeats.description',
            defaultMessage:
              'Lock teammates to read-only across the entire workspace — they can never write or be granted edit access.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.workspaceAnalytics.title',
            defaultMessage: 'Workspace analytics',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.workspaceAnalytics.description',
            defaultMessage:
              'Track adoption, doc creation, search performance, and top contributors across your workspace.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.controlInvite.title',
            defaultMessage: 'Control who can invite',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.controlInvite.description',
            defaultMessage:
              'Restrict who can invite new members to the workspace.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.controlPublic.title',
            defaultMessage: 'Control public sharing',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.controlPublic.description',
            defaultMessage: 'Restrict who can publish docs to the web.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.controlChannels.title',
            defaultMessage: 'Control who can create channels',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.controlChannels.description',
            defaultMessage: 'Restrict who can create new top-level channels.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.admin.controlGroups.title',
            defaultMessage: 'Control who can manage user groups',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.admin.controlGroups.description',
            defaultMessage:
              'Restrict who can create, edit, or delete user groups.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      id: 'security',
      title: intl.formatMessage({
        id: 'pricing.features.security.title',
        defaultMessage: 'Security & compliance',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.backups.title',
            defaultMessage: 'Automatic data backups',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.backups.description',
            defaultMessage:
              'Slite automatically backs up your workspace data on a regular schedule.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.sso.title',
            defaultMessage: 'OpenID SSO',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.sso.description',
            defaultMessage:
              'Single sign-on via OpenID identity providers like Google Workspace.',
          }),
          basic: false,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.scim.title',
            defaultMessage: 'SCIM provisioning',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.scim.description',
            defaultMessage:
              'Enterprise-grade user provisioning via SCIM (Okta, OneLogin, etc.).',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.auditLog.title',
            defaultMessage: 'Audit log',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.auditLog.description',
            defaultMessage:
              'Detailed log of security and access events for your workspace.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.soc2.title',
            defaultMessage: 'SOC 2 Type II compliance',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.soc2.description',
            defaultMessage:
              'Slite is independently audited and certified to meet SOC 2 Type II security standards.',
          }),
          basic: true,
          pro: true,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.security.hipaa.title',
            defaultMessage: 'HIPAA compliance',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.security.hipaa.description',
            defaultMessage:
              'HIPAA-eligible plan with a signed BAA for healthcare data handling.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
      ],
    },
    {
      id: 'support',
      title: intl.formatMessage({
        id: 'pricing.features.support.title',
        defaultMessage: 'Support & services',
      }),
      items: [
        {
          title: intl.formatMessage({
            id: 'pricing.features.support.priority.title',
            defaultMessage: 'Priority support',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.support.priority.description',
            defaultMessage: 'Faster response times for support requests.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.support.accountManager.title',
            defaultMessage: 'Dedicated account manager',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.support.accountManager.description',
            defaultMessage:
              'A dedicated point of contact at Slite who knows your team and use case.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.support.onboarding.title',
            defaultMessage: 'Personalized onboarding & migration',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.support.onboarding.description',
            defaultMessage:
              'Slite team helps you migrate from your existing tools, set up your workspace, and roll out company-wide.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.support.customSources.title',
            defaultMessage: 'Custom data sources, built with you',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.support.customSources.description',
            defaultMessage:
              'We will build new data source connectors for tools you rely on, alongside your team.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
        {
          title: intl.formatMessage({
            id: 'pricing.features.support.sla.title',
            defaultMessage: 'Service Level Agreement',
          }),
          description: intl.formatMessage({
            id: 'pricing.features.support.sla.description',
            defaultMessage: 'Contractual uptime and response time guarantees.',
          }),
          basic: false,
          pro: false,
          enterprise: true,
        },
      ],
    },
  ]
}
